import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Leaf, Mountain, FlaskConical } from "lucide-react";

const categoriesData = [
  {
    id: "herbal",
    label: "Herbal Honey",
    subtitle: "Ayurvedic blends • immunity",
    accent: "from-amber-400 to-orange-400",
    icon: Leaf,
    storyImage:
      "https://images.pexels.com/photos/8478058/pexels-photo-8478058.jpeg?auto=compress&cs=tinysrgb&w=800",
    storyTitle: "Herbal Honey – Nature’s Daily Dose",
    storyText:
      "Our herbal honey is slowly infused with tulsi, lemongrass, and other traditional herbs. Perfect for morning kadha, soothing teas, and everyday immunity. Each jar is crafted in small batches to preserve aroma and medicinal value.",
    products: [
      {
        id: "p1",
        name: "Tulsi Herbal Honey",
        weight: "250g",
        price: "₹299",
        image:
          "https://images.pexels.com/photos/8477766/pexels-photo-8477766.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
      {
        id: "p2",
        name: "Ayur Wellness Honey",
        weight: "500g",
        price: "₹449",
        image:
          "https://images.pexels.com/photos/3735208/pexels-photo-3735208.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
      {
        id: "p3",
        name: "Lemongrass Calm Honey",
        weight: "250g",
        price: "₹329",
        image:
          "https://images.pexels.com/photos/3735192/pexels-photo-3735192.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
    ],
  },
  {
    id: "wild",
    label: "Wild Forest",
    subtitle: "Deep, bold forest notes",
    accent: "from-amber-500 to-yellow-400",
    icon: Mountain,
    storyImage:
      "https://images.pexels.com/photos/8478057/pexels-photo-8478057.jpeg?auto=compress&cs=tinysrgb&w=800",
    storyTitle: "Wild Forest Honey – Straight from the Hills",
    storyText:
      "Collected from untouched forest regions, this honey captures the character of wildflowers and native blooms. Naturally darker and richer in minerals, it’s perfect for those who prefer bold, intense flavours.",
    products: [
      {
        id: "p4",
        name: "Western Ghats Wild Honey",
        weight: "500g",
        price: "₹499",
        image:
          "https://images.pexels.com/photos/3735153/pexels-photo-3735153.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
      {
        id: "p5",
        name: "Himalayan Forest Honey",
        weight: "250g",
        price: "₹349",
        image:
          "https://images.pexels.com/photos/8477747/pexels-photo-8477747.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
    ],
  },
  {
    id: "infused",
    label: "Infused",
    subtitle: "Dessert-style drizzles",
    accent: "from-orange-500 to-pink-500",
    icon: FlaskConical,
    storyImage:
      "https://images.pexels.com/photos/4790280/pexels-photo-4790280.jpeg?auto=compress&cs=tinysrgb&w=800",
    storyTitle: "Infused Honey – Dessert in a Jar",
    storyText:
      "Cinnamon sticks, vanilla beans, and dry fruits are infused into pure honey to create cafe-style toppings. Use it on pancakes, toast, yoghurt, desserts, and everything in between.",
    products: [
      {
        id: "p6",
        name: "Cinnamon Infused Honey",
        weight: "250g",
        price: "₹329",
        image:
          "https://images.pexels.com/photos/4110201/pexels-photo-4110201.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
      {
        id: "p7",
        name: "Vanilla Bean Honey",
        weight: "250g",
        price: "₹379",
        image:
          "https://images.pexels.com/photos/3735210/pexels-photo-3735210.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
      {
        id: "p8",
        name: "Dry Fruit Dessert Honey",
        weight: "500g",
        price: "₹549",
        image:
          "https://images.pexels.com/photos/4110096/pexels-photo-4110096.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
    ],
  },
  {
    id: "classic",
    label: "Classic",
    subtitle: "Everyday table honey",
    accent: "from-amber-300 to-orange-300",
    icon: FlaskConical,
    storyImage:
      "https://images.pexels.com/photos/3735210/pexels-photo-3735210.jpeg?auto=compress&cs=tinysrgb&w=800",
    storyTitle: "Classic Honey – For the Whole Family",
    storyText:
      "Smooth, floral and naturally sweet – this is your everyday honey for milk, bread, smoothies, and simple ‘one spoon’ cravings. Loved by kids, parents, and grandparents alike.",
    products: [
      {
        id: "p9",
        name: "Everyday Table Honey",
        weight: "500g",
        price: "₹299",
        image:
          "https://images.pexels.com/photos/3735164/pexels-photo-3735164.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
      {
        id: "p10",
        name: "Family Pack Honey",
        weight: "1kg",
        price: "₹549",
        image:
          "https://images.pexels.com/photos/8478054/pexels-photo-8478054.jpeg?auto=compress&cs=tinysrgb&w=800",
      },
    ],
  },
];

const storyVariants = {
  initial: { opacity: 0, y: 20, scale: 0.98 },
  animate: { opacity: 1, y: 0, scale: 1 },
  exit: { opacity: 0, y: -15, scale: 0.98 },
};

const productsVariants = {
  initial: { opacity: 0, y: 15 },
  animate: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: 0.05 * i, duration: 0.2 },
  }),
};

const HoneyTimelineShop = () => {
  const [activeId, setActiveId] = useState("herbal");
  const activeCategory = categoriesData.find((c) => c.id === activeId);

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-amber-50/60 to-orange-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-14">
        {/* Header */}
        <header className="mb-8 sm:mb-10 text-center">
          <p className="text-xs sm:text-sm tracking-[0.25em] uppercase text-amber-500 mb-2">
            Pure • Raw • Traceable
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-amber-900">
            Discover Your Kind of Honey
          </h1>
          <p className="mt-3 text-sm sm:text-base text-slate-600 max-w-2xl mx-auto">
            Slide through the collections, read the story behind each honey,
            and explore curated products for every mood, season, and ritual.
          </p>
        </header>

        {/* 🔥 NEW TIMELINE BAR 🔥 */}
        <section className="mb-8 sm:mb-10">
          <motion.div
            layout
            className="relative overflow-hidden rounded-3xl bg-gradient-to-r from-[#1b130d] via-[#24130c] to-[#301306] text-amber-50 shadow-xl border border-amber-900/40 px-3 sm:px-5 py-4 sm:py-5"
          >
            {/* subtle glow/honey texture */}
            <div className="pointer-events-none absolute inset-0 opacity-60 mix-blend-screen bg-[radial-gradient(circle_at_top,_#fbbf24_0,_transparent_55%),radial-gradient(circle_at_bottom,_#f97316_0,_transparent_55%)]" />

            <div className="relative z-10 mb-3 sm:mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
              <div className="flex items-center gap-2">
                <span className="inline-flex h-8 w-8 items-center justify-center rounded-2xl bg-amber-500/20 border border-amber-400/60">
                  <span className="h-2 w-2 rounded-full bg-amber-400 shadow-[0_0_12px_rgba(251,191,36,0.85)]" />
                </span>
                <div>
                  <p className="text-[11px] uppercase tracking-[0.2em] text-amber-300/90">
                    Collections
                  </p>
                  <p className="text-sm sm:text-base font-semibold text-amber-50">
                    Tap a flavour journey
                  </p>
                </div>
              </div>
              <p className="text-[11px] sm:text-xs text-amber-200/80">
                {categoriesData.length} curated ranges · single-origin raw honey
              </p>
            </div>

            {/* scrollable on mobile, segmented on desktop */}
            <div className="relative z-10 flex gap-3 sm:gap-4 overflow-x-auto no-scrollbar">
              {categoriesData.map((cat) => {
                const Icon = cat.icon;
                const isActive = cat.id === activeId;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveId(cat.id)}
                    className="relative min-w-[210px] sm:min-w-0 sm:flex-1 group text-left"
                  >
                    {/* sliding highlight card */}
                    {isActive && (
                      <motion.div
                        layoutId="tl-highlight"
                        className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${cat.accent} shadow-[0_18px_45px_rgba(0,0,0,0.55)]`}
                        transition={{ type: "spring", stiffness: 260, damping: 24 }}
                      />
                    )}

                    <div className="relative flex items-center gap-3 px-3 py-2.5 sm:px-4 sm:py-3 rounded-2xl border border-amber-500/40 bg-white/0 bg-clip-padding backdrop-blur-[1px] group-hover:border-amber-200/80 transition-colors">
                      <div
                        className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-2xl border text-sm shadow-sm ${
                          isActive
                            ? "bg-black/10 border-white/40 text-amber-50"
                            : "bg-black/30 border-amber-800/80 text-amber-200"
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div
                          className={`text-xs sm:text-sm font-semibold ${
                            isActive ? "text-white" : "text-amber-100"
                          }`}
                        >
                          {cat.label}
                        </div>
                        <div
                          className={`text-[11px] sm:text-xs ${
                            isActive ? "text-amber-100/90" : "text-amber-300/80"
                          }`}
                        >
                          {cat.subtitle}
                        </div>
                      </div>
                      <div className="hidden sm:flex flex-col items-end gap-1 text-[10px] text-amber-200/70">
                        <span className="uppercase tracking-[0.18em]">
                          {cat.id === "classic"
                            ? "Everyday"
                            : cat.id === "infused"
                            ? "Dessert"
                            : cat.id === "wild"
                            ? "Bold"
                            : "Wellness"}
                        </span>
                        <span className="h-1 w-6 rounded-full bg-amber-300/40 group-hover:bg-amber-200/80 transition-colors" />
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </motion.div>
        </section>

        {/* STORY + PRODUCTS (same idea as before, just styled) */}
        <section className="space-y-6 sm:space-y-8">
          <AnimatePresence mode="wait">
            <motion.div
              key={activeCategory.id}
              variants={storyVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              transition={{ duration: 0.25 }}
              className="flex flex-col md:flex-row items-stretch gap-4 md:gap-6 bg-white/95 backdrop-blur-sm rounded-3xl border border-amber-100 shadow-md overflow-hidden"
            >
              {/* image */}
              <div className="w-full md:w-1/3 relative">
                <div className="absolute inset-0 bg-gradient-to-br from-amber-200/20 to-orange-200/10" />
                <img
                  src={activeCategory.storyImage}
                  alt={activeCategory.storyTitle}
                  className="w-full h-48 sm:h-56 md:h-full object-cover"
                />
                <div className="absolute left-3 top-3 px-2 py-1 rounded-full bg-black/40 text-[11px] text-amber-50 uppercase tracking-wider">
                  {activeCategory.label} Collection
                </div>
              </div>

              {/* text */}
              <div className="w-full md:w-2/3 p-4 sm:p-6 md:p-7 flex flex-col justify-center">
                <div
                  className={`inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] bg-gradient-to-r ${activeCategory.accent} bg-clip-text text-transparent`}
                >
                  <span>Small-batch Crafted</span>
                  <span className="w-1 h-1 rounded-full bg-amber-400" />
                  <span>Single Origin</span>
                </div>
                <h2 className="mt-2 text-xl sm:text-2xl md:text-3xl font-bold text-amber-900">
                  {activeCategory.storyTitle}
                </h2>
                <p className="mt-3 text-sm sm:text-base text-slate-700 leading-relaxed">
                  {activeCategory.storyText}
                </p>

                <div className="mt-4 flex flex-wrap items-center gap-3 text-xs sm:text-sm text-slate-500">
                  <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-amber-50 text-amber-800 border border-amber-100">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                    100% Pure &amp; Unadulterated
                  </span>
                  <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-800 border border-orange-100">
                    <span>Perfect for</span>
                    <span className="font-semibold">
                      {activeCategory.id === "classic"
                        ? "Daily use"
                        : activeCategory.id === "infused"
                        ? "Desserts"
                        : activeCategory.id === "wild"
                        ? "Strong flavour lovers"
                        : "Immunity & wellness"}
                    </span>
                  </span>
                </div>
              </div>
            </motion.div>
          </AnimatePresence>

          {/* Products header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <h3 className="text-lg sm:text-xl font-semibold text-amber-950">
                Products in this collection
              </h3>
              <p className="text-xs sm:text-sm text-slate-500">
                Curated jars from the{" "}
                <span className="font-semibold text-amber-700">
                  {activeCategory.label}
                </span>{" "}
                range.
              </p>
            </div>
            <div className="text-xs sm:text-sm text-slate-500">
              {activeCategory.products.length} product
              {activeCategory.products.length !== 1 ? "s" : ""} · Cold-filtered ·
              Raw honey
            </div>
          </div>

          {/* Product grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {activeCategory.products.map((prod, index) => (
              <motion.div
                key={prod.id}
                custom={index}
                variants={productsVariants}
                initial="initial"
                animate="animate"
                className="group bg-white/95 border border-amber-100 rounded-2xl shadow-sm overflow-hidden flex flex-col hover:-translate-y-1 hover:shadow-lg transition-all duration-200"
              >
                <div className="relative h-28 sm:h-32 overflow-hidden">
                  <img
                    src={prod.image}
                    alt={prod.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/25 via-black/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-200" />
                  <div className="absolute left-2 top-2 text-[11px] px-2 py-0.5 rounded-full bg-black/40 text-amber-50">
                    {prod.weight}
                  </div>
                </div>
                <div className="p-2.5 sm:p-3 flex flex-col flex-1">
                  <h4 className="text-xs sm:text-sm font-semibold text-slate-900 line-clamp-2 mb-1">
                    {prod.name}
                  </h4>
                  <p className="text-[11px] sm:text-xs text-slate-500 mb-2">
                    Raw • Unfiltered • No added sugar
                  </p>
                  <div className="mt-auto flex items-center justify-between">
                    <span className="text-sm sm:text-base font-bold text-amber-700">
                      {prod.price}
                    </span>
                    <button className="text-[11px] sm:text-xs px-2.5 py-1 rounded-full bg-gradient-to-r from-amber-500 to-orange-500 text-white font-medium shadow-sm hover:shadow-md hover:brightness-105 transition-all">
                      View / Add
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
};

export default HoneyTimelineShop;
